import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { OtPosition } from "./PositionStats";
import { AreaChart } from "@mantine/charts";
import _ from "lodash";
import { useViewportSize } from "@mantine/hooks";
import { ActionIcon, List, Popover, Radio, Select, Slider, Stack, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import ProgressBar from "./ProgressBar";

interface Props {
    opening: OtPosition;
}

type Type = "win" | "draw" | "loss";

const WinDrawLossStats: React.FC<Props> = ({ opening }) => {
    const { width } = useViewportSize();
    const [movingAverageWindow, setMovingAverageWindow] = useState(
        Math.min(20, Math.ceil(opening.OtAttempt2.length / 2))
    );
    const [type, setType] = useState<Type>("win");
    const [selectedRating, setSelectedRating] = useState("all");

    useEffect(() => {
        setMovingAverageWindow(Math.min(20, Math.ceil(opening.OtAttempt2.length / 2)));
    }, [opening]);

    const attemptsByRating = opening.OtAttempt2.reduce((acc: any, attempt) => {
        acc["all"] = acc["all"] || [];
        acc["all"].push(attempt);
        if (attempt.selectedEngineLevel) {
            acc[attempt.selectedEngineLevel] = acc[attempt.selectedEngineLevel] || [];
            acc[attempt.selectedEngineLevel].push(attempt);
        } else {
            if (parseInt(attempt.engineLevel as string) >= 400 && parseInt(attempt.engineLevel as string) < 1000) {
                acc["400"] = acc["400"] || [];
                acc["400"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 1000 && parseInt(attempt.engineLevel as string) < 1200) {
                acc["1000"] = acc["1000"] || [];
                acc["1000"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 1200 && parseInt(attempt.engineLevel as string) < 1400) {
                acc["1200"] = acc["1200"] || [];
                acc["1200"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 1400 && parseInt(attempt.engineLevel as string) < 1600) {
                acc["1400"] = acc["1400"] || [];
                acc["1400"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 1600 && parseInt(attempt.engineLevel as string) < 1800) {
                acc["1600"] = acc["1600"] || [];
                acc["1600"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 1800 && parseInt(attempt.engineLevel as string) < 2000) {
                acc["1800"] = acc["1800"] || [];
                acc["1800"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 2000 && parseInt(attempt.engineLevel as string) < 2200) {
                acc["2000"] = acc["2000"] || [];
                acc["2000"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 2200 && parseInt(attempt.engineLevel as string) < 2500) {
                acc["2200"] = acc["2200"] || [];
                acc["2200"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 2500 && parseInt(attempt.engineLevel as string) < 2600) {
                acc["2500"] = acc["2500"] || [];
                acc["2500"].push(attempt);
            }
            if (parseInt(attempt.engineLevel as string) >= 2600) {
                acc["Masters"] = acc["Masters"] || [];
                acc["Masters"].push(attempt);
            }
        }
        return acc;
    }, {});

    const filteredAttempts = attemptsByRating[selectedRating] || [];

    const evalDataPointsMA = filteredAttempts
        ?.map((attempt, index, array) => {
            const evals = array.slice(index, index + movingAverageWindow).map((a) => parseFloat(a.evaluation));
            const avg =
                evals.reduce((a, b) => {
                    b = Math.min(10, Math.max(-9.75, b));
                    return a + b;
                }, 0) / evals.length;
            return {
                date: dayjs(attempt.createdAt).format("D. MMM"),
                eval: Math.round(avg * 10) / 10,
            };
        })
        .slice(0, -movingAverageWindow + 1);

    const getDataPoints = (opening: OtPosition, type: Type) => {
        if (_.isEmpty(filteredAttempts)) {
            return [];
        }

        return filteredAttempts
            ?.map((attempt, index, array) => {
                const evals = array.slice(index, index + movingAverageWindow).map((a) => parseFloat(a.evaluation));

                const winPercentage =
                    (100 *
                        evals.filter((e) => {
                            if (opening.defaultPlayerColor === "w") {
                                if (type === "win") return e >= 0.6;
                                if (type === "draw") return e > -0.1 && e < 0.6;
                                if (type === "loss") return e <= -0.1;
                            } else if (opening.defaultPlayerColor === "b") {
                                if (type === "win") return e <= -0.1;
                                if (type === "draw") return e > -0.1 && e < 0.6;
                                if (type === "loss") return e >= 0.6;
                            }
                        }).length) /
                    evals.length;

                return {
                    date: dayjs(attempt.createdAt).format("D. MMM"),
                    percentage: Math.round(winPercentage * 10) / 10,
                };
            })
            .slice(0, -movingAverageWindow + 1);
    };

    const dataPoints = getDataPoints(opening, type);

    const isMobile = width < 520;

    return (
        <div>
            <div className="flex space-x-10 [@media(max-width:900px)]:flex-col [@media(max-width:900px)]:space-y-12 [@media(max-width:900px)]:items-center [@media(max-width:900px)]:space-x-0">
                {!_.isEmpty(evalDataPointsMA) && (
                    <div className="flex justify-center">
                        <AreaChart
                            strokeWidth={3}
                            h={isMobile ? 370 : 500}
                            w={isMobile ? 370 : 500}
                            data={dataPoints}
                            dataKey="date"
                            series={[{ name: "percentage", color: "indigo.6" }]}
                            curveType="linear"
                            areaProps={{ isAnimationActive: true }}
                            gridAxis="x"
                            xAxisProps={{ interval: Math.floor(dataPoints.length / 5) }}
                            withDots={dataPoints.length <= 15}
                        />
                    </div>
                )}
                <div className="w-[320px]">
                    <div className="flex items-center justify-end space-x-1 mb-8">
                        <Popover width={350} position="bottom" withArrow shadow="md">
                            <Popover.Target>
                                <ActionIcon variant="transparent" className="ml-4">
                                    <IconInfoCircle />
                                </ActionIcon>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <Text>
                                    The graph displays the percentage of games that ended up in an advantageous, equal
                                    or disadvantageous position. <br />
                                    <br />
                                    The ranges are defined as follows:
                                </Text>
                                {opening.defaultPlayerColor === "w" && (
                                    <List listStyleType="circle">
                                        <List.Item>Advantageous: Eval above 0.6</List.Item>
                                        <List.Item>Equal: Eval between -0.1 and 0.6</List.Item>
                                        <List.Item>Disadvantageous: Eval below -0.1</List.Item>
                                    </List>
                                )}
                                {opening.defaultPlayerColor === "b" && (
                                    <List listStyleType="circle">
                                        <List.Item>Advantageous: Eval below -0.1</List.Item>
                                        <List.Item>Equal: Eval between -0.1 and 0.6</List.Item>
                                        <List.Item>Disadvantageous: Eval above 0.6</List.Item>
                                    </List>
                                )}
                            </Popover.Dropdown>
                        </Popover>
                        <Text c="dimmed" fz="xs">
                            How it works
                        </Text>
                    </div>
                    <Text c="dimmed" fz="xs" mb="xs">
                        Moving average window
                    </Text>
                    <Slider
                        value={movingAverageWindow}
                        onChange={setMovingAverageWindow}
                        step={1}
                        min={2}
                        max={Math.min(dataPoints.length - 1, 40)}
                    />
                    <Stack mt="xl">
                        <Radio checked={type === "win"} onChange={() => setType("win")} label="Advantage" />
                        <Radio checked={type === "draw"} onChange={() => setType("draw")} label="Equal" />
                        <Radio checked={type === "loss"} onChange={() => setType("loss")} label="Disadvantage" />
                    </Stack>

                    <Select
                        mt="xl"
                        label="Opponent level"
                        placeholder="Pick value"
                        data={[
                            { label: "All", value: "all" },
                            { label: "400-1000", value: "400" },
                            { label: "1000-1200", value: "1000" },
                            { label: "1200-1400", value: "1200" },
                            { label: "1400-1600", value: "1400" },
                            { label: "1600-1800", value: "1600" },
                            { label: "1800-2000", value: "1800" },
                            { label: "2000-2200", value: "2000" },
                            { label: "2200-2500", value: "2200" },
                            { label: "2500-2600", value: "2500" },
                            { label: "Masters", value: "Masters" },
                        ].filter((item) => attemptsByRating[item.value])}
                        value={selectedRating}
                        allowDeselect={false}
                        onChange={async (level) => setSelectedRating(level as string)}
                    />
                </div>
            </div>
            <div className="mt-32">
                <ProgressBar attempts={filteredAttempts} playerColor={opening.defaultPlayerColor} />
            </div>
        </div>
    );
};

export default WinDrawLossStats;
